import { useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { copyToClipboard } from "utils/commonUtility";
import SyntaxHighlighter from "react-syntax-highlighter";
import { dark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import outputImg from "../images/custompay-output.png"; // Adjust path as needed

export default function CustomPayment() {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        const codeElement = document.getElementById("customPaySampleRequestCode");
        const text = codeElement.innerText || codeElement.textContent;
        copyToClipboard(text, setCopied);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <>
            <div className="grid grid-cols-12 gap-1" id="CustomPayPage">
                <div className="col-span-12 md:col-span-8 gap-1">
                    <div>
                        <p className="font-bold text-[32px]">BenePay Custom Pay Page</p>
                    </div>
                    <div>
                        <hr className="border-t border-gray-300 my-2 w-full" />
                    </div>

                    {/* Intro Paragraph */}
                    <div id="intro" className="p-4 mb-4">
                        <p className="text-gray-700">
                            Enable your customers to make custom payments or donations using a secure, tokenized payment page.
                        </p>
                    </div>

                    {/* Integration Steps */}
                    <div className="space-y-4 mt-6" id="Integration">
                        {/* Prerequisites */}
                        <div className="p-4 bg-gray-50 border-l-4 border-blue-500 rounded">
                            <h3 className="font-semibold text-gray-800">Prerequisites</h3>
                            <ol className="list-decimal ml-5 mt-2 text-gray-600">
                                <li><b>Client ID</b> ( Unique identifier for your application )</li>
                                <li><b>Client Secret</b> ( Confidential key for authentication )</li>
                                <li><b>Authentication URL</b> ( Endpoint for access tokens )</li>
                                <li><b>Merchant ID</b> ( Unique merchant identifier )</li>
                                <li><b>Source URL</b> ( Base URL for BenePay payment pages )</li>
                            </ol>
                        </div>
                        {/* Authentication Process */}
                        <div className="p-4 bg-gray-50 border-l-4 border-green-500 rounded">
                            <h3 className="font-semibold text-gray-800">Authentication Process</h3>
                            <p>
                                <span className="text-gray-700">Auth URL (UAT):</span> <code>https://bene-collect.auth.eu-west-2.amazoncognito.com/oauth2/token</code>
                            </p>
                            <p>
                                <span className="text-gray-700">App Client Id:</span> <code>&lt;Client Id&gt;</code>
                            </p>
                            <p>
                                <span className="text-gray-700">App Client Secret:</span> <code>&lt;Client Secret&gt;</code>
                            </p>
                            <p>
                                <span className="text-gray-700">Grant Type:</span> <code>client credentials</code>
                            </p>
                            <div className="mt-3">
                                <span className="font-semibold">Sample cURL command:</span>
                                <div className="mt-2 overflow-x-auto bg-gray-900 text-white rounded-lg p-3 relative">
                                    <SyntaxHighlighter
                                        language="bash"
                                        style={dark}
                                        customStyle={{
                                            background: "transparent",
                                            margin: 0,
                                            padding: 0,
                                            fontSize: "0.95em"
                                        }}
                                    >{`curl -X POST -k \\
  -H 'Content-Type: application/x-www-form-urlencoded' \\
  -H 'Authorization: Basic <client-secret>' \\
  -i '<auth-url>' \\
  --data 'granttype=clientcredentials&clientid=<clientid>'`}
                                    </SyntaxHighlighter>
                                </div>
                            </div>
                        </div>
                        {/* Redirection Process */}
                        <div className="p-4 bg-gray-50 border-l-4 border-yellow-500 rounded">
                            <h3 className="font-semibold text-gray-800">Redirection Process</h3>
                            <p className="mt-3 mb-4">
                                <span>Once authentication process is completed frame the redirect URL as below structure.</span><br />
                                <div className="mt-2 overflow-x-auto bg-gray-900 text-white rounded-lg p-3">
                                    <pre className="whitespace-pre-wrap text-sm w-max">
                                        <code><b>&lt;source-url&gt;</b>/payment-request-initiate/<b>&lt;merchant-ID&gt;</b>?token=<b>&lt;access-token&gt;</b></code>
                                    </pre>
                                </div>
                            </p>
                            <span>Example:</span>
                            <div className="mt-2 overflow-x-auto bg-gray-900 text-white rounded-lg p-3">
                                <pre className="whitespace-pre-wrap text-sm w-max">
                                    {`https://uat-collect.benepay.io/payment-request-initiate/teslanova?token=eyJraWQiOiI9...`}
                                </pre>
                            </div>
                        </div>
                        {/* Custom Pay Page Display */}
                        <div>
                            <h3 className="font-semibold text-gray-800">Custom Pay Page Display</h3>
                            <p className="mt-2 mb-2 text-gray-700">
                                Customers will see the Custom Pay Page to enter an amount and proceed with payment.
                            </p>
                            <img
                                src={outputImg}
                                alt="WooCommerce Checkout Output"
                                className="rounded border shadow max-w-full"
                                style={{ maxHeight: 350 }}
                            />
                        </div>
                    </div>

                    {/* Sample Code */}
                    <div className="mt-9 rounded" id="SampleCode">
                        <div className="flex justify-between items-center">
                            <p className="text-gray-800 font-bold text-xl mb-2">Sample Code for Generate Token and Redirect</p>
                        </div>
                        <p className="mt-2 text-gray-700">
                            Below is a sample JavaScript code to fetch an OAuth2 access token and redirect the user to the Custom Pay Page:
                        </p>
                        <p className="mt-2 text-gray-600">
                            <b>Note:</b> Replace <code>clientId</code> and <code>clientSecret</code> with your actual credentials. The button with <code>id="sampleButton"</code> should exist in your HTML.
                        </p>
                        <div className="mt-4 overflow-x-auto bg-gray-900 text-white rounded-lg p-3 relative">
                            <button
                                onClick={handleCopy}
                                className="absolute top-2 right-2 text-gray-300 hover:text-white bg-gray-700 px-2 py-1 rounded text-xs flex items-center"
                                title={copied ? "Copied!" : "Copy"}
                            >
                                <FontAwesomeIcon icon={faCopy} />
                                <span className="ml-1">{copied ? "Copied!" : "Copy"}</span>
                            </button>
                            <SyntaxHighlighter
                                language="javascript"
                                style={dark}
                                customStyle={{
                                    background: "transparent",
                                    margin: 0,
                                    padding: 0,
                                    fontSize: "0.95em"
                                }}
                                codeTagProps={{ id: "customPaySampleRequestCode" }}
                            >{`const donateBtn = document.getElementById("sampleButton");

const clientId = '';
const clientSecret = '';

// To change the UAT environment URLs with acutal live URLs when you wants to connect with production
// Benepay will share the live URLs with you when you are ready to go live
const authUrl = 'https://bene-collect.auth.eu-west-2.amazoncognito.com/oauth2/token'
const returnUrl = 'https://uat-collect-dashboard.benepay.io';

donateBtn.addEventListener("click", function () {
    console.log("inside donate");

    const parameters = new URLSearchParams();
    parameters.append('grant_type', 'client_credentials');
    parameters.append('client_id', clientId );
    parameters.append('client_secret', clientSecret );

    fetch(authUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: parameters
    })
    .then(response => {
        if (!response.ok) throw new Error('Failed to fetch token');
        return response.json();
    })
    .then(data => {
        console.log("data", data);

        console.log("OAuth2 access token:", data.access_token);
        if (data.access_token) {
            const redirectUrl = \`\${returnUrl}/payment-request-initiate/teslanova?token=\${data.access_token}\`;
            console.log("redirectUrl", redirectUrl);

            window.location.href = redirectUrl;
        } else {
            console.error("Token is empty or invalid.");
        }
    })
    .catch(error => console.error('Error:', error));
});`}
                            </SyntaxHighlighter>
                        </div>
                    </div>
                </div>

                {/* Table of Contents */}
                <div className="hidden md:col-span-4 md:block">
                    <div className="sticky top-4 p-4">
                        <p className="text-gray-700 font-bold pl-5">TABLE OF CONTENTS</p>
                        <div>
                            <a href="#intro" className="text-gray-700 pl-9 py-1 block hover:underline">Introduction</a>
                        </div>
                        <div>
                            <a href="#Integration" className="text-gray-700 pl-9 py-1 block hover:underline">Integration Steps</a>
                        </div>
                        <div>
                            <a href="#SampleCode" className="text-gray-700 pl-9 py-1 block hover:underline">Sample Code</a>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}